"use client";

import { usePathname } from "next/navigation";
import { type CSSProperties, type ReactNode, useLayoutEffect, useState } from "react";
import { WorkspaceQueryProvider } from "../workspace/WorkspaceQueryProvider";
import { WorkspaceShell } from "../workspace/WorkspaceShell";
import { PartnersAccountShell } from "./PartnersAccountShell";
import { SupportAccountShell } from "./SupportAccountShell";
import { AppFrame } from "../ui/AppFrame";
import { EmailVerificationReminder } from "./EmailVerificationReminder";
import { BillingIntentCapture } from "./BillingIntentCapture";
import { IdentitySessionSync } from "@/app/modules/auth/components/IdentitySessionSync";

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const isPartnerRoute = pathname === "/partners" || pathname.startsWith("/partners/");
  const isSupportRoute = pathname === "/support" || pathname.startsWith("/support/");
  const isAuthLinkRoute = ["/account/security", "/account/password", "/sign-in/provider", "/verify-email", "/forgot-password", "/reset-password", "/workspace-invitation", "/mcp/authorize"].includes(pathname)
    || /^\/mcp\/actions\/[^/]+\/[^/]+\/?$/.test(pathname);
  const [reminderHeight, setReminderHeight] = useState(0);
  const height = isAuthLinkRoute ? 0 : reminderHeight;
  const shellStyle = {
    "--account-notice-height": `${height}px`,
    "--app-viewport-height": `calc(100dvh - 2 * var(--app-frame-inset) - ${height}px)`,
  } as CSSProperties;

  useLayoutEffect(() => {
    // Toasts and fullscreen overlays can render outside this shell's DOM subtree.
    document.documentElement.style.setProperty("--account-notice-height", `${height}px`);
    return () => {
      document.documentElement.style.removeProperty("--account-notice-height");
    };
  }, [height]);

  return (
    <WorkspaceQueryProvider>
      <IdentitySessionSync />
      <BillingIntentCapture />
      <div style={shellStyle}>
        {!isAuthLinkRoute ? <EmailVerificationReminder onHeightChanged={setReminderHeight} /> : null}
        <AppFrame>
          {isAuthLinkRoute || pathname === "/identity/client-portal" ? children : isPartnerRoute ? (
            <PartnersAccountShell>{children}</PartnersAccountShell>
          ) : isSupportRoute ? (
            <SupportAccountShell>{children}</SupportAccountShell>
          ) : (
            <WorkspaceShell>{children}</WorkspaceShell>
          )}
        </AppFrame>
      </div>
    </WorkspaceQueryProvider>
  );
}
